import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Text,
    Flex,
    ActionButton,
    SearchField,
    RangeSlider,
    Button,
    View,
    MenuTrigger,
    Menu,
    Item,
    DialogTrigger,
    Dialog,
    Heading,
    Divider,
    Content,
} from '@adobe/react-spectrum';
import './FilterPanel.css';
import { ManaSelector } from './ManaSelector';
import Filter from '@spectrum-icons/workflow/Filter';
import ChevronUp from '@spectrum-icons/workflow/ChevronUp';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import Close from '@spectrum-icons/workflow/Close';
import { ARCHETYPE_MAJORS, getArchetypeMinorsForMajor } from '../../data/archetypeOptions';
import { buildDefaultFilters, RANGE_AXES, DEFAULT_RANGES, getRangeAxesForContext } from '../../data/filterDefaults';

const POWER_PRESETS = [
    { label: 'Casual', min: 1, max: 4 },
    { label: 'Focused', min: 5, max: 7 },
    { label: 'High', min: 8, max: 10 },
    { label: 'Any', min: 1, max: 10 },
];

// Per-context spec — what extras (colors, archetypes, label overrides) the
// panel renders. The list of range axes itself lives in filterDefaults.js
// alongside the slider bounds and sentinel rules so all three sources stay
// in lockstep.
const CONTEXT_SPECS = {
    decks: {
        showColors: true,
        showArchetypes: true,
    },
    users: {
        showColors: false,
        showArchetypes: false,
        rangeLabelOverrides: {
            power: 'Avg power',
            bracket: 'Avg bracket',
            salt: 'Avg salt',
        },
    },
    commanders: {
        showColors: true,
        showArchetypes: false,
    },
};

const isAxisDefault = (axis, range) => {
    if (!range) return true;
    const def = DEFAULT_RANGES[axis];
    if (!def) return true;
    return (range.min ?? null) === def.min && (range.max ?? null) === def.max;
};

function countActiveFilters(filters, context) {
    if (!filters) return 0;
    const spec = CONTEXT_SPECS[context] || CONTEXT_SPECS.decks;
    const rangeAxes = getRangeAxesForContext(context);
    let count = 0;
    if (filters.query) count += 1;
    if (spec.showColors && filters.colorFilters) count += 1;
    if (spec.showArchetypes) {
        if (filters.archetypeMajor && filters.archetypeMajor !== 'any') count += 1;
        if (filters.archetypeMinor && filters.archetypeMinor !== 'any') count += 1;
    }
    rangeAxes.forEach((axis) => {
        if (!isAxisDefault(axis, filters?.ranges?.[axis])) count += 1;
    });
    return count;
}

function FilterClearBadge({ activeCount, context, onClear }) {
    if (activeCount <= 0) return null;
    const handleClear = () => {
        if (onClear) onClear(buildDefaultFilters(context));
    };
    return (
        <Flex direction="row" alignItems="center" UNSAFE_className="filterTriggerBadgeGroup">
            <span className="filterCountBadge" aria-label={`${activeCount} active filters`}>
                {activeCount}
            </span>
            <ActionButton
                UNSAFE_className="filterClearButton"
                onPress={handleClear}
                aria-label="Clear all filters"
                isQuiet
            >
                <Close />
            </ActionButton>
        </Flex>
    );
}

export function FilterPanelTrigger({ filters, isOpen, onToggle, onClear, context = 'decks' }) {
    const activeCount = useMemo(() => countActiveFilters(filters, context), [filters, context]);
    return (
        <Flex direction="row" alignItems="center" UNSAFE_className="filterTriggerGroup">
            <ActionButton
                marginStart={10}
                UNSAFE_className={`resultsToolbarButton filterTriggerButton ${isOpen ? 'is-expanded' : ''}`}
                onPress={onToggle}
                aria-expanded={isOpen}
                aria-label="Toggle filters"
            >
                Filters
                <Filter />
                <span className="filterTriggerChevron">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
            </ActionButton>
            <FilterClearBadge activeCount={activeCount} context={context} onClear={onClear} />
        </Flex>
    );
}

export function FilterPanelPopover({
    filters,
    context = 'decks',
    isOpen,
    onOpenChange,
    onFiltersChange,
    onClear,
    hideQueryInput,
}) {
    const activeCount = useMemo(() => countActiveFilters(filters, context), [filters, context]);
    const handleClear = () => {
        if (onClear) {
            onClear(buildDefaultFilters(context));
        } else if (onFiltersChange) {
            onFiltersChange(buildDefaultFilters(context));
        }
    };
    return (
        <Flex direction="row" alignItems="center" UNSAFE_className="filterTriggerGroup">
            <DialogTrigger
                type="popover"
                placement="bottom start"
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                hideArrow
            >
                <ActionButton
                    marginStart={10}
                    UNSAFE_className={`resultsToolbarButton filterTriggerButton ${isOpen ? 'is-expanded' : ''}`}
                    aria-label="Toggle filters"
                >
                    Filters
                    <Filter />
                    <span className="filterTriggerChevron">{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
                </ActionButton>
                <Dialog UNSAFE_className="resultsFilterPopoverDialog" size="L">
                    <Heading>Filters</Heading>
                    <Divider />
                    <Content>
                        <FilterPanelBody
                            isOpen
                            onClose={() => onOpenChange && onOpenChange(false)}
                            onFiltersChange={onFiltersChange}
                            filters={filters}
                            context={context}
                            hideQueryInput={hideQueryInput}
                            transparentContainer
                        />
                    </Content>
                </Dialog>
            </DialogTrigger>
            <FilterClearBadge activeCount={activeCount} context={context} onClear={handleClear} />
        </Flex>
    );
}

const readRange = (filters, axis) => {
    const r = filters?.ranges?.[axis];
    if (r) return { min: r.min ?? null, max: r.max ?? null };
    return { ...DEFAULT_RANGES[axis] };
};

export function FilterPanelBody(props) {
    const { isOpen, onClose } = props;
    const context = props?.context || 'decks';
    const spec = CONTEXT_SPECS[context] || CONTEXT_SPECS.decks;
    const rangeAxes = useMemo(() => getRangeAxesForContext(context), [context]);

    const initialQuery = props.filters?.query || '';
    const initialColors = props?.filters?.colorFilters || null;
    const initialMajor = props.filters?.archetypeMajor || 'any';
    const initialMinor = props.filters?.archetypeMinor || 'any';

    const [query, setQuery] = useState(initialQuery);
    const [colorFilters, setColorFilters] = useState(initialColors);
    const [archetypeMajor, setArchetypeMajor] = useState(initialMajor);
    const [archetypeMinor, setArchetypeMinor] = useState(initialMinor);
    const [ranges, setRanges] = useState(() => {
        const seed = {};
        rangeAxes.forEach((axis) => {
            seed[axis] = readRange(props.filters, axis);
        });
        return seed;
    });

    // Power and Bracket are mutually exclusive — only one axis is sent on
    // submit. Default the active axis to whichever is currently set; tab
    // toggles preserve the inactive value (kept in `ranges`) but are
    // stripped before the request is built so the API only sees one.
    const initialActiveScale = !isAxisDefault('bracket', readRange(props.filters, 'bracket')) ? 'bracket' : 'power';
    const [activeScale, setActiveScale] = useState(initialActiveScale);

    // Refs for the Power/Bracket tabs so arrow-key nav can move focus
    // between them (ARIA Authoring Practices §3.27).
    const scaleTabRefs = useRef({});
    const handleScaleTablistKeyDown = useCallback(
        (evn) => {
            if (evn.key !== 'ArrowLeft' && evn.key !== 'ArrowRight' && evn.key !== 'Home' && evn.key !== 'End') {
                return;
            }
            evn.preventDefault();
            const next = activeScale === 'power' ? 'bracket' : 'power';
            setActiveScale(next);
            const el = scaleTabRefs.current[next];
            if (el && typeof el.focus === 'function') el.focus();
        },
        [activeScale]
    );

    // Re-seed local state from props whenever the panel re-opens — the parent
    // is the source of truth for already-applied filters and the user might
    // have closed the panel without applying.
    useEffect(() => {
        if (isOpen) {
            setQuery(props.filters?.query || '');
            setColorFilters(props?.filters?.colorFilters || null);
            setArchetypeMajor(props.filters?.archetypeMajor || 'any');
            setArchetypeMinor(props.filters?.archetypeMinor || 'any');
            const seed = {};
            rangeAxes.forEach((axis) => {
                seed[axis] = readRange(props.filters, axis);
            });
            setRanges(seed);
            setActiveScale(!isAxisDefault('bracket', readRange(props.filters, 'bracket')) ? 'bracket' : 'power');
        }
    }, [isOpen, props.filters, rangeAxes]);

    const updateRange = (axis, partial) => {
        setRanges((prev) => ({ ...prev, [axis]: { ...prev[axis], ...partial } }));
    };

    const buildFiltersPayload = (overrides = {}) => {
        // Power & Bracket share the same UI slot — strip the inactive one
        // back to defaults *only* in the outgoing payload so the search
        // doesn't apply both range filters at once. The local `ranges`
        // state preserves both axes' values so toggling tabs round-trips
        // their last-set range instead of nuking it.
        const cleanedRanges = { ...ranges };
        if (activeScale === 'power') {
            cleanedRanges.bracket = { ...DEFAULT_RANGES.bracket };
        } else {
            cleanedRanges.power = { ...DEFAULT_RANGES.power };
        }
        return {
            query: overrides.query !== undefined ? overrides.query : query,
            colorFilters: spec.showColors ? colorFilters : '',
            archetypeMajor: spec.showArchetypes ? archetypeMajor : '',
            archetypeMinor: spec.showArchetypes ? archetypeMinor : '',
            ranges: cleanedRanges,
        };
    };

    const pushFiltersChangeUpstream = () => {
        props?.onFiltersChange && props.onFiltersChange(buildFiltersPayload());
    };

    const submitFilters = (latestQuery) => {
        // Enter inside the search field fires before the controlled `query`
        // state catches the latest keystroke — accept the verbatim value as
        // an override so the search applies what the user actually typed.
        props?.onFiltersChange &&
            props.onFiltersChange(buildFiltersPayload({ query: latestQuery !== undefined ? latestQuery : query }));
        onClose && onClose();
    };

    const pushResetFilters = () => {
        const fresh = buildDefaultFilters(context);
        setQuery('');
        setColorFilters(null);
        setArchetypeMajor('any');
        setArchetypeMinor('any');
        setRanges(fresh.ranges);
        props?.onFiltersChange && props.onFiltersChange(fresh);
    };

    const applyPowerPreset = (preset) => {
        updateRange('power', { min: preset.min, max: preset.max });
    };

    const handleColorFiltersChange = (evn) => setColorFilters(evn.colors);
    const setAllColors = () => setColorFilters('wubrg');
    const clearColors = () => setColorFilters(null);

    const colorCount = colorFilters ? colorFilters.replace('c', '').length : 0;
    const isColorless = colorFilters === 'c';

    const sideMargin = props?.transparentContainer ? 0 : 10;

    const renderSliderAxis = (axis) => {
        const cfg = RANGE_AXES[axis];
        const range = ranges[axis] || DEFAULT_RANGES[axis];
        const min = range.min ?? cfg.sliderMin;
        const max = range.max ?? cfg.sliderMax;
        const isDefault = isAxisDefault(axis, range);
        const maxLabel = cfg.maxIsOpenEnded && max >= cfg.sliderMax ? `${cfg.sliderMax}+` : max;
        return (
            <div key={axis}>
                <Flex direction="row" justifyContent="flex-end" alignItems="baseline">
                    <span className={`filterValueReadout ${isDefault ? 'is-muted' : ''}`}>
                        {min} – {maxLabel}
                    </span>
                </Flex>
                <RangeSlider
                    UNSAFE_className="searedRangeSlider"
                    onChange={(evn) => updateRange(axis, { min: evn.start, max: evn.end })}
                    marginStart={0}
                    marginTop={6}
                    labelPosition="side"
                    label=" "
                    width="100%"
                    minValue={cfg.sliderMin}
                    maxValue={cfg.sliderMax}
                    value={{ start: min, end: max }}
                />
                {axis === 'power' && (
                    <div className="searedChipRow">
                        {POWER_PRESETS.map((preset) => {
                            const active = preset.min === min && preset.max === max;
                            return (
                                <button
                                    key={preset.label}
                                    type="button"
                                    className={`searedChip ${active ? 'is-active' : ''}`}
                                    onClick={() => applyPowerPreset(preset)}
                                >
                                    {preset.label}
                                    <span className="searedChipRange">
                                        {preset.min}–{preset.max}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderArchetypePicker = () => {
        const minors = getArchetypeMinorsForMajor(archetypeMajor === 'any' ? null : archetypeMajor);
        const hasSelection = archetypeMajor !== 'any' || archetypeMinor !== 'any';
        return (
            <Flex direction="column" UNSAFE_className="searedFilterSection" key="archetype">
                <Flex direction="row" justifyContent="space-between" alignItems="baseline">
                    <Text UNSAFE_className="filtersSectionLabel">Archetype</Text>
                    <span className={`filterValueReadout ${hasSelection ? '' : 'is-muted'}`}>
                        {hasSelection ? `${archetypeMajor} · ${archetypeMinor}` : 'Any'}
                    </span>
                </Flex>
                <div className="searedArchPickerRow">
                    <MenuTrigger>
                        <ActionButton UNSAFE_className="searedArchPicker">{archetypeMajor.toUpperCase()}</ActionButton>
                        <Menu
                            items={ARCHETYPE_MAJORS}
                            selectionMode="single"
                            selectedKeys={[archetypeMajor]}
                            onSelectionChange={(keys) => {
                                const next = Array.from(keys)[0] || 'any';
                                setArchetypeMajor(next);
                                // Reset minor when major changes — current minor may not belong to the new major.
                                setArchetypeMinor('any');
                            }}
                        >
                            {(item) => <Item key={item.id}>{item.label.toUpperCase()}</Item>}
                        </Menu>
                    </MenuTrigger>
                    <span className="searedArchPickerSep">/</span>
                    <MenuTrigger>
                        <ActionButton UNSAFE_className="searedArchPicker">{archetypeMinor.toUpperCase()}</ActionButton>
                        <Menu
                            items={minors}
                            selectionMode="single"
                            selectedKeys={[archetypeMinor]}
                            onSelectionChange={(keys) => {
                                setArchetypeMinor(Array.from(keys)[0] || 'any');
                            }}
                        >
                            {(item) => <Item key={item.id}>{item.label.toUpperCase()}</Item>}
                        </Menu>
                    </MenuTrigger>
                </div>
            </Flex>
        );
    };

    // Panel-level Enter handler — Spectrum's SearchField/RangeSlider/NumberField
    // each swallow Enter at the field level, but anything else (chips, color
    // picker, custom buttons) gets to bubble up here so a stray keypress
    // applies the filter set instead of doing nothing.
    const handlePanelKeyDown = useCallback(
        (evn) => {
            if (evn.key !== 'Enter' || evn.defaultPrevented) return;
            const tag = evn.target?.tagName;
            // Don't double-fire when the user just pressed Enter on a
            // <button>; the button's own onClick already ran.
            if (tag === 'BUTTON') return;
            submitFilters();
        },
        [activeScale, query, colorFilters, archetypeMajor, archetypeMinor, ranges]
    );

    return (
        <div
            className={`searedInlineFilter ${isOpen ? 'is-open' : ''}`}
            aria-hidden={!isOpen}
            style={{ marginLeft: sideMargin, marginRight: sideMargin }}
            onKeyDown={handlePanelKeyDown}
        >
            <div className="searedInlineFilter__inner">
                <div className="searedFilterSections">
                    <View
                        isHidden={props?.hideQueryInput}
                        UNSAFE_className="searedFilterSection searedFilterSection--search"
                    >
                        <Text UNSAFE_className="filtersSectionLabel">Search</Text>
                        <SearchField
                            width="100%"
                            onChange={setQuery}
                            onSubmit={submitFilters}
                            isDisabled={props?.hideQueryInput}
                            value={query}
                            placeholder="Search title, commander, or user…"
                            marginTop={6}
                            marginBottom={10}
                        />
                    </View>

                    {(rangeAxes.includes('power') || rangeAxes.includes('bracket')) && (
                        <Flex direction="column" UNSAFE_className="searedFilterSection">
                            <Text UNSAFE_className="filtersSectionLabel">Filter by</Text>
                            <div
                                className="searedSegmented searedSegmented--scale"
                                role="tablist"
                                aria-label="Filter by power or bracket"
                                style={{ marginTop: 6 }}
                                onKeyDown={handleScaleTablistKeyDown}
                            >
                                {['power', 'bracket'].map((axis) => {
                                    const cfg = RANGE_AXES[axis];
                                    const label = spec.rangeLabelOverrides?.[axis] || cfg.label;
                                    const active = activeScale === axis;
                                    return (
                                        <button
                                            key={axis}
                                            type="button"
                                            role="tab"
                                            aria-selected={active}
                                            tabIndex={active ? 0 : -1}
                                            ref={(el) => {
                                                scaleTabRefs.current[axis] = el;
                                            }}
                                            className={`searedSegment ${active ? 'is-active' : ''}`}
                                            onClick={() => setActiveScale(axis)}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            <div style={{ marginTop: 10 }}>{renderSliderAxis(activeScale)}</div>
                        </Flex>
                    )}

                    {rangeAxes.map((axis) => {
                        if (axis === 'power' || axis === 'bracket') return null;
                        const cfg = RANGE_AXES[axis];
                        if (!cfg) return null;
                        const label = spec.rangeLabelOverrides?.[axis] || cfg.label;
                        return (
                            <Flex direction="column" UNSAFE_className="searedFilterSection" key={axis}>
                                <Text UNSAFE_className="filtersSectionLabel">{label}</Text>
                                <div style={{ marginTop: 6 }}>{renderSliderAxis(axis)}</div>
                            </Flex>
                        );
                    })}

                    {spec.showArchetypes && !props?.disableArchetypes && renderArchetypePicker()}

                    {spec.showColors && (
                        <Flex direction="column" UNSAFE_className="searedFilterSection">
                            <Flex direction="row" justifyContent="space-between" alignItems="baseline">
                                <Text UNSAFE_className="filtersSectionLabel">Color identity</Text>
                                <span className={`filterValueReadout ${!colorFilters ? 'is-muted' : ''}`}>
                                    {isColorless ? 'Colorless' : colorCount === 0 ? 'Any' : `${colorCount} selected`}
                                </span>
                            </Flex>
                            <ManaSelector
                                handleColorFilterChange={handleColorFiltersChange}
                                colorFilters={colorFilters}
                            />
                            <div className="searedChipRow">
                                <button type="button" className="searedChip searedChip--ghost" onClick={setAllColors}>
                                    All five
                                </button>
                                <button
                                    type="button"
                                    className="searedChip searedChip--ghost"
                                    onClick={clearColors}
                                    disabled={!colorFilters}
                                >
                                    Clear
                                </button>
                            </div>
                        </Flex>
                    )}
                </div>

                <div className="searedFilterFooter">
                    <button
                        type="button"
                        className="searedTextLink"
                        onClick={() => {
                            pushResetFilters();
                            onClose && onClose();
                        }}
                    >
                        Reset all
                    </button>
                    <Flex direction="row" gap="size-100">
                        <Button variant="secondary" onPress={() => onClose && onClose()}>
                            Cancel
                        </Button>
                        <Button
                            variant="accent"
                            onPress={() => {
                                pushFiltersChangeUpstream();
                                onClose && onClose();
                            }}
                        >
                            Apply
                        </Button>
                    </Flex>
                </div>
            </div>
        </div>
    );
}
